import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { UsersService } from '../../services/users-service.service';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../abuse/action-confirm-dialog/action-confirm-dialog.component';
import {
  NudgeReach,
  ProfileFieldDropOffDto,
  ProfileFunnelDto,
  ProfileNudgeAllDto,
  ProfileNudgeStatsDto,
  StalledProfileDto,
} from '../../data-transfer-objects/profile-funnel.dto';

/**
 * How many accounts are still just a name, and what is stopping them.
 *
 * Every account is created at sign-in from the auth provider's display name, so
 * an abandoned onboarding leaves a permanent name-only profile. Those profiles
 * are now filtered out of Nearby and AI matches, which fixes what everyone else
 * sees but not the accounts themselves — this page is for the second half.
 *
 * The stage counts are computed from the profile fields as they stand, so they
 * are true for every account ever created. The timings are not: nothing was
 * recorded before the funnel migration, so a cohort's median is taken over the
 * crossings actually measured and the page says how many that was rather than
 * quietly averaging a smaller number.
 */
@Component({
  selector: 'app-profile-funnel',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PersonLinkComponent,
  ],
  templateUrl: './profile-funnel.component.html',
  styleUrls: ['./profile-funnel.component.scss'],
})
export class ProfileFunnelComponent implements OnInit {
  report: ProfileFunnelDto | null = null;
  loading = true;
  error: string | null = null;
  notice: string | null = null;
  /** The account whose nudge is in flight, if any. */
  busyId: string | null = null;
  nudgedIds = new Set<string>();
  /** The whole-list send, from the dry run through to the result. */
  nudgingAll = false;

  constructor(private usersService: UsersService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.usersService.getProfileFunnel().subscribe({
      next: (report) => {
        this.report = report;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load the funnel: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  percent(part: number, whole: number): string {
    if (!whole) return '—';
    return `${Math.round((part / whole) * 100)}%`;
  }

  /*
   * Fields sorted by the count that actually points somewhere.
   *
   * Someone who bounced off the editor is missing everything and implicates no
   * field in particular; someone who got through it and stopped is pointing at
   * the field they stopped on. That second group is the order.
   */
  get fieldsByDropOff(): ProfileFieldDropOffDto[] {
    return [...(this.report?.fields ?? [])].sort(
      (a, b) => b.missingAmongStarted - a.missingAmongStarted
    );
  }

  conversionRate(n: ProfileNudgeStatsDto): string {
    return this.percent(n.convertedUsers, n.users);
  }

  /** Hours as something readable; the numbers span an hour to several weeks. */
  duration(hours: number | null): string {
    if (hours == null) return '—';
    if (hours < 1) return '<1h';
    if (hours < 48) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  }

  /*
   * The list is ghosts only, so what is missing is always one of the six that
   * keep them out of Nearby. Showing all of it would be a wall of text in a
   * table cell; the count carries the rest.
   */
  missingSummary(row: StalledProfileDto): string {
    const shown = row.missing.slice(0, 3).join(', ');
    const rest = row.missing.length - 3;
    return rest > 0 ? `${shown} +${rest}` : shown;
  }

  reachLabel(reach: NudgeReach): string {
    return reach === 'push' ? 'push' : reach === 'email' ? 'email' : 'unreachable';
  }

  nudge(row: StalledProfileDto): void {
    this.busyId = row.id;
    this.notice = null;
    this.error = null;
    this.usersService.sendProfileNudge(row.id).subscribe({
      next: (result) => {
        this.busyId = null;
        this.nudgedIds.add(row.id);
        this.notice =
          result.sent === 'push'
            ? `Pushed a reminder to ${row.name || row.id}.`
            : result.tokenCleared
              ? `Their push token was dead — cleared it and emailed ${row.email} as Sharon instead.`
              : `Emailed ${row.email} as Sharon.`;
      },
      error: (err) => {
        this.busyId = null;
        this.error =
          err?.status === 409
            ? `Could not nudge ${row.name || row.id}: ${err?.error?.error ?? 'nothing to send to'}`
            : `Could not nudge ${row.name || row.id}: ${err?.message ?? err}`;
      },
    });
  }

  /*
   * Everyone in the list, in one click - but never blind. The click dry-runs
   * first and puts the counts and the exact copy in front of the admin; only
   * Confirm sends. The dry run's push count is a ceiling: a token only proves
   * it is dead by being sent to, so the result after Confirm can show fewer
   * pushes and more emails than the preview did, and says why.
   */
  nudgeAll(): void {
    if (this.nudgingAll) return;
    this.nudgingAll = true;
    this.notice = null;
    this.error = null;

    this.usersService
      .nudgeAllStalled(true)
      .pipe(
        switchMap((preview) => {
          const due = preview.push + preview.email;
          if (!due) {
            this.notice = this.previewSummary(preview) + ' Nothing to send.';
            return of(undefined);
          }
          return this.confirm({
            title: `Remind ${due} stalled account${due === 1 ? '' : 's'}?`,
            description: this.previewText(preview),
            confirmLabel: `Send ${due}`,
          }).pipe(
            switchMap((result) =>
              result?.confirmed ? this.usersService.nudgeAllStalled(false) : of(undefined)
            )
          );
        })
      )
      .subscribe({
        next: (sent) => {
          this.nudgingAll = false;
          if (!sent) return;
          this.notice = this.resultText(sent);
          this.load();
        },
        error: (err) => {
          this.nudgingAll = false;
          this.error = `Could not nudge the list: ${err?.error?.error ?? err?.message ?? err}`;
        },
      });
  }

  private previewSummary(p: ProfileNudgeAllDto): string {
    const skipped = p.skippedCooldown + p.skippedCap;
    return (
      `${p.targets} stalled: up to ${p.push} by push, ${p.email} by email, ` +
      `${p.unreachable} unreachable, ${skipped} skipped (${p.skippedCooldown} within ` +
      `the 14-day cooldown, ${p.skippedCap} already reminded twice).`
    );
  }

  private previewText(p: ProfileNudgeAllDto): string {
    return [
      this.previewSummary(p),
      '',
      'Push first; a token FCM reports dead is cleared and that person gets the email ' +
        'instead, so the push count above is a ceiling.',
      '',
      'Push — ' + p.copy.pushTitle,
      '“' + p.copy.pushBody + '”',
      '',
      'Email — from Sharon, subject “' + p.copy.emailSubjectExample + '”, naming the ' +
        'fields each person is missing.',
    ].join('\n');
  }

  private resultText(r: ProfileNudgeAllDto): string {
    const parts = [`Pushed ${r.push}`, `emailed ${r.email}`];
    if (r.deadTokens) {
      parts.push(`cleared ${r.deadTokens} dead push token${r.deadTokens === 1 ? '' : 's'}`);
    }
    if (r.unreachable) parts.push(`${r.unreachable} unreachable`);
    if (r.failed) parts.push(`${r.failed} failed`);
    const skipped = r.skippedCooldown + r.skippedCap;
    if (skipped) parts.push(`${skipped} skipped`);
    return parts.join(', ') + '.';
  }

  /** The shared confirm dialog; undefined when it was dismissed. */
  private confirm(data: ActionConfirmData) {
    return this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(
        ActionConfirmDialogComponent,
        { data, autoFocus: false }
      )
      .afterClosed();
  }
}
